/* eslint-disable no-console */
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import _uniq from 'lodash/uniq';
import { applaunchService } from '@bedrock/fido-sdk';
import { mediaEngineConfig } from '@bedrock/media-engine';
import config from '@bedrock-rdk/config';
import { getFeatureFlipping } from '../features/features.actions';
import { getDeviceId, getHomeId, getLocalDeviceId, getLocalHomeId } from '../user/user.selectors';
import { updateFidoConfig } from './fido.utils';
import { getCustomizerFeature } from '../features/features.selectors';
import { errorAction } from '../error/error.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { setupI18NOverrides } from '../../modules/i18n';
import { setUser } from '../analytics/analytics.utils';

export const CONFIG_INIT = 'app/CONFIG_INIT';

const patchAnalyticsEngines = (result, extension) => {
  if (!result.analytics) {
    result.analytics = {};
  }

  // The engines array should be replaced, not merged
  result.analytics.engines = _get(extension, 'analytics.engines') || result.analytics.engines || [];
  if (!result.analytics.engines.includes('youbora')) {
    result.analytics.engines.push('youbora');
  }
};

export const configInitApp = config => ({
  type: CONFIG_INIT,
  config,
});

/**
 * Fetch and return the customized applaunch config for a specific device
 * In case of error, return the config sent as param
 *
 * @param {String} deviceId
 * @param {Object} applaunchRemoteConfig
 * @returns {Promise<*>}
 */
const fetchAppLaunchCustomizer = (deviceId, applaunchConfig) => {
  try {
    const featureFlipping = getFeatureFlipping(
      config.bannedFeatures,
      config.featureFlipping,
      applaunchConfig.featureFlipping,
    );
    const { customizerFeature } = featureFlipping;

    if (customizerFeature && !config.bannedFeatures.includes('customizerFeature')) {
      return applaunchService.getCustomization({ localDeviceId: deviceId }).catch(error => {
        console.error(`[app.config.actions] Error while fetching customized config: ${error.message}`);

        return applaunchConfig;
      });
    }

    return Promise.resolve(applaunchConfig);
  } catch (error) {
    return Promise.resolve(applaunchConfig);
  }
};

const applyExternalConfig = (localConfig, externalConfig) => {
  const featureFlipping = getFeatureFlipping(
    config.bannedFeatures,
    config.featureFlipping,
    externalConfig.featureFlipping,
  );

  const enhancedConfig = _merge(localConfig, { ...externalConfig, featureFlipping });
  updateFidoConfig(enhancedConfig);

  // The engines array should be replaced, not merged
  patchAnalyticsEngines(enhancedConfig, externalConfig);

  return enhancedConfig;
};

const getABTestFlags = config =>
  (config ? _uniq((config.deviceCustomizerVariants || []).concat(config.gigyaCustomizerVariants || [])) : []).join(',');

export const fetchAppLaunchConfig = config => async (dispatch, getState) => {
  const state = getState();
  const deviceId = getLocalDeviceId(state);
  const applaunchConfig = await applaunchService.getApplaunch().catch(() => ({}));
  const externalConfig = await fetchAppLaunchCustomizer(`deviceid-${deviceId}`, applaunchConfig);
  const enhancedConfig = applyExternalConfig(config, externalConfig);

  mediaEngineConfig.init({
    lang: enhancedConfig.language,
    isDebug: Boolean(enhancedConfig.debug),
  });

  setupI18NOverrides(enhancedConfig.translationsOverrides);
  dispatch(configInitApp(enhancedConfig));
};

/**
 * Refresh the configuration with the one from customizer
 *
 * @param {*} id
 */
export const refreshCustomizedConfig = user => async (dispatch, getState) => {
  const state = getState();

  if (getCustomizerFeature(state)) {
    const uid = user?.uid || getHomeId(state) || getDeviceId(state) || getLocalHomeId(state) || getLocalDeviceId(state);

    try {
      const externalConfig = await applaunchService.getCustomization({ uid });
      const enhancedConfig = applyExternalConfig(config, externalConfig);

      setUser({
        profile: user,
        variants: getABTestFlags(enhancedConfig),
      });

      mediaEngineConfig.set('lang', enhancedConfig.language);
      mediaEngineConfig.set('isDebug', Boolean(enhancedConfig.debug));

      patchAnalyticsEngines(enhancedConfig, externalConfig);

      dispatch(configInitApp(enhancedConfig));
    } catch (e) {
      dispatch(
        errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.GENERAL, 'Failed to get customization data on config refresh'),
      );
    }
  }
};
